import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@/components/Elements';
import {
  EmailMarketingProducts,
  PrepaidProducts,
  SMSMarketingProducts,
} from '@/components/Products';
import stepsFixtureEmailing from '@/components/Products/fixtures/marksEmail.json';
import stepsFixturePrepaid from '@/components/Products/fixtures/marksPrepaidEmail.json';
import stepsFixtureSms from '@/components/Products/fixtures/marksPrepaidSms.json';
import {
  PRODUCT_CATEGORY_LITE,
  PRODUCT_CATEGORY_PREPAID_EMAIL,
  PRODUCT_CATEGORY_PREPAID_SMS,
  PRODUCT_CATEGORY_PRO,
  PRODUCT_CATEGORY_TOTAL,
  PRODUCT_CATEGORY_TYPE_EMAIL,
  PRODUCT_CATEGORY_TYPE_PREPAID,
  PRODUCT_CATEGORY_TYPE_SMS,
} from '@/config/appConstants';
import { Country } from '@/features/countries';
import { SmsCredits } from '@/features/smsCredits';
import { useUserProducts } from '@/hooks/useUserProducts';
import { UserProductTypes, ProductCategoryTypes, ProductTypes } from '@/types';
import { getBillingModeByTargetTap, isCoreProduct } from '@/utils/helpers';

const ProductsContainerWrap = styled.div`
  .md__tabscontainer__button {
    background-color: var(--md-bg-gray-color);
    border-radius: 15px 15px 0 0;
    color: var(--md-mediumGray-color);
    text-transform: uppercase;
    font-weight: bold;
  }
  .md__tabscontainer__button.active {
    background-color: var(--md-yellow-color);
    color: var(--md-blue-color);
    font-weight: 900 !important;
  }
`;

const getButtonStyles = () =>
  'md__tabscontainer__button py-4 px-0.5 mr-0 sm:px-14 sm:mr-2 sm:py-5 text-xs lg:text-base';

export const getProductsByEmailMarketing = (products: ProductTypes[]): ProductTypes[][] => {
  const filteredProducts = products.filter((product: ProductTypes) => isCoreProduct(product));

  if (!filteredProducts) return [];

  let liteProducts: ProductTypes[] = [];
  let proProducts: ProductTypes[] = [];
  let totalProducts: ProductTypes[] = [];

  filteredProducts.forEach((product: ProductTypes) => {
    if (product.category === PRODUCT_CATEGORY_LITE) liteProducts = [...liteProducts, product];
    if (product.category === PRODUCT_CATEGORY_PRO) proProducts = [...proProducts, product];
    if (product.category === PRODUCT_CATEGORY_TOTAL) totalProducts = [...totalProducts, product];
  });

  return [[...liteProducts], [...proProducts], [...totalProducts]];
};

const getProductsByProductCategoryType = (
  products: ProductTypes[],
  productCategoryType: string
): ProductTypes[][] => [
  products && products.filter((product: ProductTypes) => product.category === productCategoryType),
];

export type ProductsContainerProps = {
  userIsLogged?: boolean;
  products: ProductTypes[];
  freemiumProduct: ProductTypes;
  userProducts?: UserProductTypes[] | undefined;
  onRequestInfo: (product: ProductTypes) => void;
  onProductSelect: (product: ProductTypes) => void;
  onFreeProductSelect?: (product: ProductTypes) => void;
  tabSelected: ProductCategoryTypes;
  setTabSelected: any;
  className?: string;
  countries: Country[];
  defaultSMSCountryProvider?: string;
  smsCredits: SmsCredits;
};

export const ProductsContainer = ({
  userIsLogged,
  products,
  freemiumProduct,
  userProducts,
  onRequestInfo,
  onProductSelect,
  onFreeProductSelect,
  tabSelected,
  setTabSelected,
  className,
  countries,
  defaultSMSCountryProvider,
  smsCredits,
}: ProductsContainerProps) => {
  const { t } = useTranslation();
  const getEmailMarketingSteps = () => stepsFixtureEmailing;
  const getSMSMarketingSteps = () => stepsFixtureSms;
  const getPrepaidSteps = () => stepsFixturePrepaid;
  const { setSelectedProductBillingMode } = useUserProducts();

  const showBody = (target: ProductCategoryTypes) => {
    setSelectedProductBillingMode(getBillingModeByTargetTap(target));
    setTabSelected(target);
  };

  const getTabContent = (
    tabSelected: ProductCategoryTypes,
    productsItems: ProductTypes[],
    userProducts: UserProductTypes[] | undefined
  ) => {
    let tabContent;
    switch (tabSelected) {
      case PRODUCT_CATEGORY_TYPE_EMAIL: {
        const productsEmailMarketing = getProductsByEmailMarketing(productsItems);

        tabContent = (
          <EmailMarketingProducts
            freemiumProduct={freemiumProduct}
            products={productsEmailMarketing}
            steps={getEmailMarketingSteps()}
            onRequestInfo={onRequestInfo}
            onProductSelect={onProductSelect}
            onFreeProductSelect={onFreeProductSelect}
            userProducts={userProducts}
            userIsLogged={userIsLogged}
          />
        );
        break;
      }
      case PRODUCT_CATEGORY_TYPE_SMS: {
        const productsPrepaidSms = getProductsByProductCategoryType(
          productsItems,
          PRODUCT_CATEGORY_PREPAID_SMS
        );
        tabContent = (
          <SMSMarketingProducts
            products={productsPrepaidSms}
            steps={getSMSMarketingSteps()}
            onRequestInfo={onRequestInfo}
            onProductSelect={onProductSelect}
            userProducts={userProducts}
            countries={countries}
            defaultSMSCountryProvider={defaultSMSCountryProvider}
            smsCredits={smsCredits}
            userIsLogged={userIsLogged}
          />
        );
        break;
      }
      case PRODUCT_CATEGORY_TYPE_PREPAID: {
        const productsPrepaidEmail = getProductsByProductCategoryType(
          productsItems,
          PRODUCT_CATEGORY_PREPAID_EMAIL
        );
        tabContent = (
          <PrepaidProducts
            products={productsPrepaidEmail}
            steps={getPrepaidSteps()}
            onRequestInfo={onRequestInfo}
            onProductSelect={onProductSelect}
            userProducts={userProducts}
            userIsLogged={userIsLogged}
          />
        );
        break;
      }
    }

    return <div>{tabContent}</div>;
  };

  return (
    <ProductsContainerWrap>
      <div className={`flex justify-center ${className}`}>
        <Button
          size="free"
          className={clsx(getButtonStyles(), {
            active: tabSelected === PRODUCT_CATEGORY_TYPE_EMAIL,
          })}
          onClick={() => showBody(PRODUCT_CATEGORY_TYPE_EMAIL)}
          type="button"
          variant="raw"
          title={t('productTypes.emailMarketing.title')}
        >
          {t('productTypes.emailMarketing.title')}
        </Button>
        <Button
          className={clsx(getButtonStyles(), {
            active: tabSelected === PRODUCT_CATEGORY_TYPE_SMS,
          })}
          onClick={() => showBody(PRODUCT_CATEGORY_TYPE_SMS)}
          size="md"
          type="button"
          variant="raw"
          title={t('productTypes.smsMarketing.title')}
        >
          {t('productTypes.smsMarketing.title')}
        </Button>
        <Button
          className={clsx(getButtonStyles(), 'mr-0', {
            active: tabSelected === PRODUCT_CATEGORY_TYPE_PREPAID,
          })}
          onClick={() => showBody(PRODUCT_CATEGORY_TYPE_PREPAID)}
          size="md"
          type="button"
          variant="raw"
          title={t('productTypes.prepaid.title')}
        >
          {t('productTypes.prepaid.title')}
        </Button>
      </div>
      <div className="bg-md-yellow pt-0 pb-24 px-1 lg:px-12">
        {getTabContent(tabSelected, products, userProducts)}
      </div>
    </ProductsContainerWrap>
  );
};
